import { createTheme } from '@mui/material/styles';
import { indigo, grey } from '@mui/material/colors'

const jsiTheme = createTheme({
    // The keyboard focus ring (2.4.7, 1.4.11), drawn by MUI 9.4+ on every
    // focusable control it ships. Before 9.4 this was five hand-written
    // Mui-focusVisible overrides (ButtonBase, Button, IconButton, Tab, Link) plus
    // a picker-icon one that never fired, and any control not on that list had
    // no ring. MUI also insets the ring where its own containers would clip it
    // (Tab, MenuItem, ...), which we used to do by hand for Tab.
    //
    // Black rather than MUI's default primary.main: 21:1 on the paper against
    // 4.6:1, and it is the colour the hand-written rings already used. The rest
    // is MUI's default -- a 2px solid outline at a 2px offset.
    focusVisible: {
        outlineColor: '#000000'
    },
    palette: {
        background: {
            paper: "#fbffff"
        },
        secondary: {
            main: "#D50000"
        }
    },
    typography: {
        h1: {
            textAlign: "center",
            fontSize: "1.25rem",
            marginTop: '.1rem'
        },
        body1: {
            fontSize: "1rem"
        }
    },
    components: {
        // MUI v9 changed the ListItemIcon default min-width from 56px to 36px,
        // which pulled the Home step list and the Privacy bullets 13px left.
        // Pinned to the old value so the v9 upgrade changes no layout. Delete
        // this block to adopt v9's tighter default.
        MuiListItemIcon: {
            styleOverrides: {
                root: {
                    minWidth: '56px'
                }
            }
        },
        // x-date-pickers v8 stopped rendering the visible field as an <input>
        // and now draws editable sections into a container div. The pickers
        // pass `sx: classes.textbox` through slotProps.textField.inputProps,
        // which still reaches the (now non-sizing) hidden input, so the fields
        // silently rendered at 56px against the 32px of every TextField beside
        // them. These values mirror classes.textbox in styles/styles.js; keep
        // them in step. Done here rather than at the four call sites so every
        // picker stays consistent.
        MuiPickersInputBase: {
            styleOverrides: {
                // Nested under root rather than targeting the sectionsContainer
                // slot directly: MUI's own rule for that slot is more specific,
                // so a slot-level override applies the height but loses the
                // padding, leaving the field 4px taller than its neighbours.
                root: {
                    '& .MuiPickersInputBase-sectionsContainer': {
                        height: '.2rem',
                        paddingTop: '.9rem',
                        paddingBottom: '.9rem',
                        // The container is a flex row and defaults to stretching
                        // its section spans to the .2rem height above, which
                        // squashes the line box and drops the digits to the
                        // bottom of the field. Centering lets each section keep
                        // its natural height and sit on the field's centre line.
                        alignItems: 'center'
                    }
                }
            }
        },
        // CPDEV-2824 (1.4.11): MUI outlines a text field in rgba(0,0,0,0.23),
        // which over the #fbffff paper is #C1C4C4 -- 1.74:1, where the boundary
        // that tells a user "this is a field you can type in" needs 3:1. The
        // border is the only thing that identifies these fields: they have no
        // fill of their own. #767676 is 4.5:1 here and is already the app's
        // accessible grey (the date-of-birth placeholder uses it).
        //
        // Two keys, not one: the date pickers do not use MuiOutlinedInput. They
        // ship their own PickersOutlinedInput with its own copy of the same
        // rgba(0,0,0,0.23) default, so a single override would leave every date
        // field on the old colour. Hover (text.primary), focus (primary.main)
        // and disabled (exempt) keep MUI's own colours -- those rules are
        // descendant selectors and still outrank this one.
        MuiOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#767676'
                }
            }
        },
        // CPDEV-2832 (1.4.11): the picker's year and month buttons are native
        // <button>s, not ButtonBase, so the focusVisible ring above never reaches
        // them. MUI X sets outline:0 and marks focus only with a 12% black fill --
        // #DCE0E0 on the paper, 1.32:1. Give them the same ring as everything else
        // (theme.focusVisible is the resolved ring: 2px solid black, 2px offset).
        // The day buttons are ButtonBase and already get it.
        MuiYearCalendar: {
            styleOverrides: {
                button: ({theme}) => ({'&:focus-visible': theme.focusVisible})
            }
        },
        MuiMonthCalendar: {
            styleOverrides: {
                button: ({theme}) => ({'&:focus-visible': theme.focusVisible})
            }
        },
        MuiPickersOutlinedInput: {
            styleOverrides: {
                notchedOutline: {
                    borderColor: '#767676'
                }
            }
        },
        // CPDEV-2819 (1.4.3): on keyboard focus MUI pulses a "focus ripple" --
        // a disc of currentColor at 30% opacity, painted OVER the label because
        // TouchRipple is position:absolute with z-index 0. The text keeps its
        // colour while its background becomes a 30% tint of that same colour,
        // so every primary-coloured control fails contrast while focused:
        //   tab / Back  #1976D2 on #B7D6F2  4.57:1 -> 3.05:1
        //   Search      #FFFFFF on #5E9FE0  4.60:1 -> 2.80:1
        // (measured on QA; the auditor reported #B7D6F1 at 3.047:1). The ripple
        // is not our focus indicator -- theme.focusVisible below is -- so it is
        // turned off on every control that forces it on, rather than recoloured.
        // Ripples on click (disableRipple) are untouched.
        //
        // Keep these even though the theme now has a focus ring: the ring does
        // not suppress the ripple, and deleting them re-opens CPDEV-2819.
        MuiButton: {
            defaultProps: {
                disableFocusRipple: true
            },
            styleOverrides: {
                // CPDEV-2824 (1.4.11): MUI draws the outlined border at 50%
                // alpha, so Back's edge was rgba(25,118,210,.5) over the paper
                // -- #8ABBE9, 2.02:1. currentColor is the button's own label
                // colour at full opacity, so the border matches the text
                // (4.57:1 for primary) and follows the color prop instead of
                // pinning one palette entry here.
                outlined: {
                    borderColor: 'currentColor'
                }
            }
        },
        MuiIconButton: {
            defaultProps: {
                disableFocusRipple: true
            }
        },
        MuiTab: {
            defaultProps: {
                disableFocusRipple: true
            }
        },
        // The label of a checkbox or radio sits outside the ButtonBase, so its
        // ripple never covers text -- but it does tint the control's own glyph,
        // which 1.4.11 holds to 3:1. Same treatment, for one focus convention
        // across the app.
        MuiCheckbox: {
            defaultProps: {
                disableFocusRipple: true
            }
        },
        MuiRadio: {
            defaultProps: {
                disableFocusRipple: true
            }
        }
    },
    overrides: {
        MuiPaper: {
            root: {
                backgroundColor: grey[50],
                lineHeight: "1",
            },
        },
        MuiTableCell: {
            head: {
                color: indigo[500]
            },
            root: {
                padding: '.1rem',
            }
        },
        MuiListItem: {
            root: {
                paddingTop: '.1px',
                paddingBottom: '.1px',
            },
            button: {
                textDecoration: "none",
                textDecorationColor: "white"
            }
        }
    }
})
export default jsiTheme
